import * as React from "react";

import {
  DEFAULT_TIME_DISPLAY_MODE,
  TIME_DISPLAY_MODES,
  TimeDisplayMode,
} from "@/shared/components/visualization/WorkspaceCtx/TimeDisplayMode";
import { EARLIEST_TIME_NS_FIELD, useViewingContext } from "@/shared/services";

import { isEpochNsString } from "./timeAnchor";
import { VisualizerClock } from "./timestampFormat";

/**
 * The registered visualizer's time display, or undefined when none is open.
 *
 * A subscription, not a snapshot: the reader switching the workspace's
 * bottom-left mode picker, or loading a file that starts earlier, moves the
 * transcript's rendering with it — which is what "Follow the visualizer" means.
 *
 * Its presence is also what makes Follow selectable at all, so it reports a
 * clock for any open workspace, falling back to the visualizer's own default
 * mode when the workspace has not persisted a choice.
 */
export function useVisualizerClock(): VisualizerClock | undefined {
  const viewingContext = useViewingContext();
  const state = viewingContext.visualizer?.state;

  // The state bag is untyped — a workspace config that survived a schema
  // round trip, plus whatever else the workspace publishes — so both reads are
  // validated here rather than trusted. An unrecognised mode string is treated
  // as no preference, not as a reason to hide Follow.
  const publishedMode = (state?.settings as { timeDisplayMode?: unknown })
    ?.timeDisplayMode;
  const mode = isTimeDisplayMode(publishedMode)
    ? publishedMode
    : DEFAULT_TIME_DISPLAY_MODE;

  // Epoch nanoseconds exceed Number.MAX_SAFE_INTEGER, so the workspace
  // publishes its earliest time as a decimal string. `BigInt` reads "" as 0n
  // and accepts negatives, either of which would offer a 1970 instant as the
  // visualizer's t=0.
  const publishedEarliest = state?.[EARLIEST_TIME_NS_FIELD];
  const earliestNs =
    typeof publishedEarliest === "string" && isEpochNsString(publishedEarliest)
      ? publishedEarliest
      : undefined;

  const open = state !== undefined;

  // Keyed on the two strings rather than the clock so identity is stable while
  // the workspace holds the same files in the same mode: the clock reaches the
  // render path of every timestamp in the transcript, and a fresh object each
  // render would re-run all of them.
  return React.useMemo(
    () =>
      open
        ? {
            mode,
            earliestNs:
              earliestNs === undefined ? undefined : BigInt(earliestNs),
          }
        : undefined,
    [open, mode, earliestNs],
  );
}

function isTimeDisplayMode(value: unknown): value is TimeDisplayMode {
  return (
    typeof value === "string" &&
    (TIME_DISPLAY_MODES as readonly string[]).includes(value)
  );
}
